/**
 * Daily Momentum™ — the diary behind the daily mission.
 *
 * One book holds two things: the activities you've chosen (each with its own
 * schedule — every day, certain weekdays, or a single date) and a log for every
 * day you've shown up, with what you ticked off and what you wrote.
 *
 * Nothing is overwritten at midnight. Yesterday stays readable, and the streak
 * is read back out of the log rather than kept as a separate running total.
 */

export type TaskSchedule =
  | { kind: "daily" }
  /** 0 = Sunday … 6 = Saturday */
  | { kind: "weekly"; days: number[] }
  | { kind: "once"; date: string };

export interface DailyTask {
  id: string;
  label: string;
  xp: number;
  schedule: TaskSchedule;
  createdAt: number;
  /** Retired activities stay in the book so old days still read correctly. */
  archivedAt?: number;
}

export interface DayLog {
  date: string;
  doneIds: string[];
  note?: string;
  savedAt: number;
}

export interface DailyBook {
  version: 2;
  tasks: DailyTask[];
  /** Keyed by YYYY-MM-DD. */
  logs: Record<string, DayLog>;
}

/** Legacy shape: a single day of checkboxes, wiped every midnight. */
interface LegacyDaily {
  date: string;
  tasks: { label: string; done: boolean }[];
}

export const DEFAULT_TASK_XP = 15;

export const DEFAULT_DAILY_LABELS = [
  "Complete a focused Python / domain lesson",
  "Practice a 3-minute presentation",
  "Read for 20 minutes",
  "Exercise for 20 minutes",
  "Journal one insight from today",
  "Meditate for 5 minutes",
  "Message one mentor or peer",
];

export const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const newId = () => Math.random().toString(36).slice(2, 10);

export function todayKey(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function shiftDay(date: string, delta: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  dt.setDate(dt.getDate() + delta);
  return todayKey(dt);
}

export function weekdayOf(date: string): number {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1).getDay();
}

export function formatDay(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  const today = todayKey();
  if (date === today) return "Today";
  if (date === shiftDay(today, -1)) return "Yesterday";
  if (date === shiftDay(today, 1)) return "Tomorrow";
  return dt.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

export function describeSchedule(s: TaskSchedule): string {
  if (s.kind === "daily") return "Every day";
  if (s.kind === "once") return `Once — ${formatDay(s.date)}`;
  if (!s.days.length) return "No days chosen";
  if (s.days.length === 7) return "Every day";
  return s.days
    .slice()
    .sort((a, b) => a - b)
    .map((d) => WEEKDAY_LABELS[d])
    .join(" · ");
}

export function emptyBook(): DailyBook {
  return {
    version: 2,
    tasks: DEFAULT_DAILY_LABELS.map((label, i) => ({
      id: `seed-${i}`,
      label,
      xp: DEFAULT_TASK_XP,
      schedule: { kind: "daily" },
      createdAt: Date.now(),
    })),
    logs: {},
  };
}

/** Accept either shape from storage or the cloud and always return a book. */
export function normaliseBook(raw: unknown): DailyBook {
  if (!raw || typeof raw !== "object") return emptyBook();
  const obj = raw as Partial<DailyBook> & Partial<LegacyDaily>;

  if (Array.isArray(obj.tasks) && obj.tasks.length && typeof obj.tasks[0] === "object"
    && "done" in (obj.tasks[0] as object)) {
    // Legacy day → seed the activity list and keep that day as the first entry.
    const legacy = obj as LegacyDaily;
    const tasks: DailyTask[] = legacy.tasks.map((t, i) => ({
      id: `seed-${i}`,
      label: t.label,
      xp: DEFAULT_TASK_XP,
      schedule: { kind: "daily" as const },
      createdAt: Date.now(),
    }));
    const doneIds = tasks.filter((_, i) => legacy.tasks[i]?.done).map((t) => t.id);
    const logs: Record<string, DayLog> = {};
    if (legacy.date && doneIds.length) {
      logs[legacy.date] = { date: legacy.date, doneIds, savedAt: Date.now() };
    }
    return { version: 2, tasks, logs };
  }

  const tasks = (Array.isArray(obj.tasks) ? obj.tasks : []).map((t) => {
    const task = t as DailyTask;
    const schedule: TaskSchedule =
      task.schedule && (task.schedule.kind === "daily" || task.schedule.kind === "weekly" || task.schedule.kind === "once")
        ? task.schedule
        : { kind: "daily" };
    return {
      id: task.id || newId(),
      label: String(task.label ?? "").slice(0, 160),
      xp: typeof task.xp === "number" ? task.xp : DEFAULT_TASK_XP,
      schedule,
      createdAt: typeof task.createdAt === "number" ? task.createdAt : Date.now(),
      archivedAt: typeof task.archivedAt === "number" ? task.archivedAt : undefined,
    } satisfies DailyTask;
  }).filter((t) => t.label.length > 0);

  const rawLogs = (obj.logs && typeof obj.logs === "object" ? obj.logs : {}) as Record<string, DayLog>;
  const logs: Record<string, DayLog> = {};
  for (const [date, log] of Object.entries(rawLogs)) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    logs[date] = {
      date,
      doneIds: Array.isArray(log?.doneIds) ? log.doneIds.filter((x) => typeof x === "string") : [],
      note: typeof log?.note === "string" ? log.note : undefined,
      savedAt: typeof log?.savedAt === "number" ? log.savedAt : Date.now(),
    };
  }

  return { version: 2, tasks: tasks.length ? tasks : emptyBook().tasks, logs };
}

export function isDueOn(task: DailyTask, date: string): boolean {
  if (task.archivedAt && todayKey(new Date(task.archivedAt)) < date) return false;
  if (task.schedule.kind === "daily") return true;
  if (task.schedule.kind === "once") return task.schedule.date === date;
  return task.schedule.days.includes(weekdayOf(date));
}

export function tasksForDate(book: DailyBook, date: string): DailyTask[] {
  const log = book.logs[date];
  return book.tasks.filter((t) => isDueOn(t, date) || log?.doneIds.includes(t.id));
}

export function logFor(book: DailyBook, date: string): DayLog {
  return book.logs[date] ?? { date, doneIds: [], savedAt: 0 };
}

export function toggleTask(book: DailyBook, date: string, taskId: string): DailyBook {
  const log = logFor(book, date);
  const has = log.doneIds.includes(taskId);
  const doneIds = has ? log.doneIds.filter((id) => id !== taskId) : [...log.doneIds, taskId];
  return {
    ...book,
    logs: { ...book.logs, [date]: { ...log, date, doneIds, savedAt: Date.now() } },
  };
}

export function setNote(book: DailyBook, date: string, note: string): DailyBook {
  const log = logFor(book, date);
  return {
    ...book,
    logs: { ...book.logs, [date]: { ...log, date, note, savedAt: Date.now() } },
  };
}

export function addTask(book: DailyBook, label: string, schedule: TaskSchedule): DailyBook {
  const clean = label.trim().slice(0, 160);
  if (!clean) return book;
  return {
    ...book,
    tasks: [
      ...book.tasks,
      { id: newId(), label: clean, xp: DEFAULT_TASK_XP, schedule, createdAt: Date.now() },
    ],
  };
}

export function updateTask(book: DailyBook, id: string, patch: Partial<Pick<DailyTask, "label" | "schedule">>): DailyBook {
  return {
    ...book,
    tasks: book.tasks.map((t) =>
      t.id === id
        ? { ...t, ...patch, label: patch.label !== undefined ? patch.label.trim().slice(0, 160) || t.label : t.label }
        : t,
    ),
  };
}

/** Retire an activity without erasing the days it was part of. */
export function archiveTask(book: DailyBook, id: string): DailyBook {
  return {
    ...book,
    tasks: book.tasks.map((t) => (t.id === id ? { ...t, archivedAt: Date.now() } : t)),
  };
}

export function restoreTask(book: DailyBook, id: string): DailyBook {
  return {
    ...book,
    tasks: book.tasks.map((t) => (t.id === id ? { ...t, archivedAt: undefined } : t)),
  };
}

export function isDayComplete(book: DailyBook, date: string): boolean {
  const due = book.tasks.filter((t) => isDueOn(t, date));
  if (!due.length) return false;
  const done = logFor(book, date).doneIds;
  return due.every((t) => done.includes(t.id));
}

/**
 * The streak, read back from the log: consecutive complete days ending today or
 * yesterday. Days with nothing scheduled are skipped rather than breaking it.
 */
export function computeStreak(book: DailyBook, today = todayKey()): { count: number; last: string } {
  let cursor = isDayComplete(book, today) ? today : shiftDay(today, -1);
  let last = "";
  let count = 0;
  for (let i = 0; i < 400; i += 1) {
    const due = book.tasks.filter((t) => isDueOn(t, cursor));
    if (due.length === 0) {
      cursor = shiftDay(cursor, -1);
      continue;
    }
    if (!isDayComplete(book, cursor)) break;
    if (!last) last = cursor;
    count += 1;
    cursor = shiftDay(cursor, -1);
  }
  return { count, last };
}

/** Logged days, newest first — the diary index. */
export function historyEntries(book: DailyBook): Array<DayLog & { total: number; complete: boolean }> {
  return Object.values(book.logs)
    .filter((l) => l.doneIds.length > 0 || (l.note ?? "").trim().length > 0)
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .map((l) => ({
      ...l,
      total: book.tasks.filter((t) => isDueOn(t, l.date)).length,
      complete: isDayComplete(book, l.date),
    }));
}

// =============================================================
// Backup — take the diary with you
// =============================================================

export interface DiaryExport {
  kind: "wekit.daily-diary";
  version: 2;
  exportedAt: string;
  book: DailyBook;
}

export const DIARY_EXPORT_KIND = "wekit.daily-diary";

export function exportPayload(book: DailyBook): DiaryExport {
  return { kind: DIARY_EXPORT_KIND, version: 2, exportedAt: new Date().toISOString(), book };
}

export function exportFilename(now = new Date()): string {
  return `wekit-daily-diary-${todayKey(now)}.json`;
}

/**
 * Read a backup file. Anything that isn't one of ours is refused by name
 * rather than silently half-imported.
 */
export function parseImport(text: string): { book: DailyBook } | { error: string } {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { error: "That file isn't valid JSON. Choose the .json backup you exported." };
  }
  const obj = raw as Partial<DiaryExport> & Partial<DailyBook>;
  const candidate = obj?.kind === DIARY_EXPORT_KIND ? obj.book : raw;
  if (!candidate || typeof candidate !== "object") {
    return { error: "That file doesn't look like a diary backup." };
  }
  const book = normaliseBook(candidate);
  if (!Object.keys(book.logs).length && !book.tasks.length) {
    return { error: "That backup has no activities or days in it." };
  }
  return { book };
}

/**
 * Merge a backup into what's already here. Nothing is destroyed: activities
 * union by id (then by label, so a re-seeded list doesn't duplicate), and a day
 * present in both keeps every tick from either side.
 */
export function mergeBooks(current: DailyBook, incoming: DailyBook): DailyBook {
  const tasks = [...current.tasks];
  const byId = new Set(tasks.map((t) => t.id));
  const byLabel = new Map(tasks.map((t) => [t.label.toLowerCase(), t.id]));
  const remap = new Map<string, string>();

  for (const t of incoming.tasks) {
    const existingId = byLabel.get(t.label.toLowerCase());
    if (existingId) {
      remap.set(t.id, existingId);
      continue;
    }
    if (byId.has(t.id)) {
      const fresh = `${t.id}-${Math.random().toString(36).slice(2, 6)}`;
      remap.set(t.id, fresh);
      tasks.push({ ...t, id: fresh });
      byId.add(fresh);
      byLabel.set(t.label.toLowerCase(), fresh);
      continue;
    }
    tasks.push(t);
    byId.add(t.id);
    byLabel.set(t.label.toLowerCase(), t.id);
  }

  const logs: Record<string, DayLog> = { ...current.logs };
  for (const [date, log] of Object.entries(incoming.logs)) {
    const mappedDone = log.doneIds.map((id) => remap.get(id) ?? id);
    const mine = logs[date];
    if (!mine) {
      logs[date] = { ...log, doneIds: mappedDone };
      continue;
    }
    const note = (mine.note ?? "").trim().length >= (log.note ?? "").trim().length
      ? mine.note
      : log.note;
    logs[date] = {
      date,
      doneIds: Array.from(new Set([...mine.doneIds, ...mappedDone])),
      note,
      savedAt: Math.max(mine.savedAt, log.savedAt),
    };
  }

  return { version: 2, tasks, logs };
}

// =============================================================
// Finding things again
// =============================================================

export type HistoryScope = "all" | "complete" | "incomplete" | "with-notes";

export interface HistoryFilter {
  query: string;
  /** An activity id, or "all". */
  taskId: string;
  scope: HistoryScope;
  /** Inclusive YYYY-MM-DD bounds, empty for open-ended. */
  from?: string;
  to?: string;
}

export const EMPTY_HISTORY_FILTER: HistoryFilter = { query: "", taskId: "all", scope: "all" };

export type HistoryEntry = DayLog & { total: number; complete: boolean };

export function filterHistory(
  book: DailyBook,
  entries: HistoryEntry[],
  f: HistoryFilter,
): HistoryEntry[] {
  const q = f.query.trim().toLowerCase();
  const labelOf = new Map(book.tasks.map((t) => [t.id, t.label.toLowerCase()]));
  return entries.filter((e) => {
    if (f.from && e.date < f.from) return false;
    if (f.to && e.date > f.to) return false;
    if (f.taskId !== "all" && !e.doneIds.includes(f.taskId)) return false;
    if (f.scope === "complete" && !e.complete) return false;
    if (f.scope === "incomplete" && e.complete) return false;
    if (f.scope === "with-notes" && !(e.note ?? "").trim()) return false;
    if (!q) return true;
    if ((e.note ?? "").toLowerCase().includes(q)) return true;
    if (e.date.includes(q)) return true;
    return e.doneIds.some((id) => (labelOf.get(id) ?? "").includes(q));
  });
}

// =============================================================
// The year at a glance
// =============================================================

export interface HeatCell {
  date: string;
  done: number;
  total: number;
  /** 0 → nothing, 1 → every scheduled activity ticked. */
  ratio: number;
  future: boolean;
  hasNote: boolean;
}

/**
 * Weeks of cells, oldest first, each week starting Sunday — the shape a
 * calendar heatmap needs, with today in the final column.
 */
export function heatmapWeeks(book: DailyBook, weeks = 18, today = todayKey()): HeatCell[][] {
  const endPad = 6 - weekdayOf(today);
  const end = shiftDay(today, endPad);
  const start = shiftDay(end, -(weeks * 7 - 1));

  const cells: HeatCell[] = [];
  for (let d = start; d <= end; d = shiftDay(d, 1)) {
    const log = book.logs[d];
    const total = book.tasks.filter((t) => isDueOn(t, d)).length;
    const done = log ? log.doneIds.length : 0;
    cells.push({
      date: d,
      done,
      total,
      ratio: total ? Math.min(1, done / total) : done > 0 ? 1 : 0,
      future: d > today,
      hasNote: Boolean((log?.note ?? "").trim()),
    });
  }

  const out: HeatCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) out.push(cells.slice(i, i + 7));
  return out;
}
